<script>
import { mapActions } from 'vuex'

import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart } from 'echarts/charts'
import {
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  LegendComponent,
  LegendScrollComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  DatasetComponent,
} from 'echarts/components'
import MChart, { THEME_KEY } from 'vue-echarts'

use([
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  LegendComponent,
  LegendScrollComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  DatasetComponent,
  CanvasRenderer,
  BarChart,
  LineChart,
])

export default {
  components: {
    MChart,
  },
  props: {
    playerData: {
      type: Array,
      default: () => [],
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      option: {
        legend: {
          data: ['Opening Value', 'Closing Value'],
        },
        tooltip: {
          trigger: 'axis',
        },
        grid: {
          top: 30,
          bottom: 10,
          left: 40,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: [],
        },
        yAxis: {
          show: true,
          offset: 20,
          type: 'value',
          inverse: false,
          splitLine: {
            show: true,
          },
          axisLabel: {
            show: true,
            formatter: function(value) {
              return value + ' M'
            },
          },
          scale: true,
          min: 40, // will be overwritten by the min of the data
          max: 160, // will be overwritten by the max of the data
        },
        series: [
          {
            name: 'Closing Value',
            type: 'line',
            smooth: true,
            labelLayout: {
              hideOverlap: true,
            },
            itemStyle: {
              color: '#72CEFF',
              barBorderRadius: 0,
            },
            label: {
              show: true,
              position: 'bottom',
              formatter: '{c}',
              fontSize: 10,
              color: this.$style['text-color'],
              offset: [0, 0],
            },
            data: [],
          },
        ],
      },
    }
  },
  computed: {},
  provide: {
    [THEME_KEY]: 'light',
  },
  watch: {
    playerData: {
      handler() {
        this.setOptionsWithPlayerData()
      },
    },
  },
  mounted() {
    this.setOptionsWithPlayerData()
  },
  methods: {
    ...mapActions('players', ['getPlayerStatData']),
    onResize() {
      if (typeof this.$refs.chart !== 'undefined') this.$refs.chart.resize()
    },
    setOptionsWithPlayerData() {
      const data = this.playerData.map((data) => {
        return {
          ...data,
          day: data['@timestamp'].split('T')[0],
        }
      })
      const minMax = this.getMinMax(data)
      const max = ((minMax.max + minMax.range * 1.5) * 1.01).toFixed(0)
      const min = (minMax.min - minMax.range).toFixed(0)

      this.option.yAxis.min = Number.parseInt(min)
      this.option.yAxis.max = Number.parseInt(max)

      this.option.xAxis.data = data.map((data) => data.day)

      this.option.series[0].data = data.map((data) => data.value.toFixed(2))
      this.isLoading = false
    },
    getMinMax(rawData) {
      const allValues = rawData.map((timeValue) => {
        return timeValue.value
      })
      const min = Math.min(...allValues)
      const max = Math.max(...allValues)

      return { min, max, range: max - min }
    },
  },
}
</script>

<template>
  <v-responsive
    v-resize="onResize"
    aspect-ratio="4/3"
    :class="$style.responsive"
  >
    <MChart
      v-if="!isLoading"
      ref="chart"
      :class="$style.chart"
      :option="option"
    />
  </v-responsive>
</template>

<style lang="scss" module>
@import '@design';

:export {
  text-color: $color-text;
}

.chart {
  margin: 0 auto;

  @media screen and (min-width: $screen-md) {
    width: 520px;
    height: 200px;
  }

  @media screen and (min-width: $screen-lg) {
    width: 700px;
    height: 400px;
  }
}

.responsive {
  height: 100%;
  overflow: visible;

  @media screen and (max-width: $screen-lg) {
    min-height: 12rem;
  }
}
</style>
